import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Smartphone, KeyRound, RotateCcw, ShieldCheck } from 'lucide-react';

/**
 * Customer account flow (modal / page / order):
 * phone -> (exists ? password [+forgot] : otp -> new password)
 * Calls onDone({ phone, token, user }) when the account is ready.
 * All errors surfaced in Persian via toastify only.
 */
const inStyle = { width: '100%', padding: '13px 14px', borderRadius: 14, border: '1.5px solid var(--border)', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.92rem', outline: 'none', background: 'var(--muted)', color: 'var(--fg)', transition: 'border-color 0.15s' };
const primaryBtn = { width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontSize: '1em', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 20px rgba(28,25,23,0.22)' };
const linkBtn = { background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.85em', fontWeight: 600, fontFamily: 'inherit', padding: 0 };
const stepLabel = { fontSize: '0.72rem', color: 'var(--muted-fg)', marginTop: 6, textAlign: 'center' };

/** Backend (English) → Persian user-facing errors */
const FA_ERRORS = {
  'Invalid credentials.': 'شماره موبایل یا رمز عبور اشتباه است.',
  'Invalid or expired code.': 'کد تایید اشتباه است یا منقضی شده؛ کد جدید بگیرید.',
  'Account already exists. Please log in.': 'این شماره قبلاً ثبت شده؛ لطفاً وارد شوید.',
  'Phone number not verified.': 'شماره هنوز تایید نشده؛ ابتدا کد تایید را وارد کنید.',
  'Invalid national code.': 'کد ملی معتبر نیست؛ دوباره بررسی کنید.',
  'Network Error': 'مشکل اتصال به اینترنت؛ دوباره تلاش کنید.',
  'timeout of 0ms exceeded': 'اتصال کند است؛ دوباره تلاش کنید.',
  'Request failed with status code 500': 'خطای سرور؛ چند لحظه دیگر تلاش کنید.',
  'Request failed with status code 419': 'نشست منقضی شد؛ صفحه را رفرش کنید.',
  'Request failed with status code 429': 'تعداد تلاش زیاد شد؛ چند دقیقه صبر کنید.',
  'Too Many Attempts.': 'تعداد درخواست زیاد شد؛ ۱۵ دقیقه صبر کنید و دوباره تلاش کنید.',
};
function faError(raw) {
  const m = String(raw || '');
  if (FA_ERRORS[m]) return FA_ERRORS[m];
  if (/[آ-ی]/.test(m)) return m;
  const low = m.toLowerCase();
  if (low.includes('password')) return 'رمز عبور معتبر نیست (حداقل ۶ حرف لازم است).';
  if (low.includes('phone') || low.includes('mobile')) return 'شماره موبایل معتبر نیست.';
  if (low.includes('code') || low.includes('otp')) return 'کد تایید اشتباه است؛ کد جدید بگیرید.';
  if (low.includes('national')) return 'کد ملی معتبر نیست.';
  if (low.includes('token') || low.includes('unauthor') || low.includes('401') || low.includes('419')) return 'نشست منقضی شد؛ صفحه را رفرش کنید و دوباره تلاش کنید.';
  if (low.includes('network') || low.includes('timeout') || low.includes('failed')) return 'مشکل اتصال؛ اینترنت را بررسی و دوباره تلاش کنید.';
  return 'خطا در انجام عملیات؛ ورودی‌ها را بررسی و دوباره تلاش کنید.';
}

/** Modern LTR digit boxes */
function OtpBoxes({ value, onChange, length = 5 }) {
  const refs = React.useRef([]);
  const [focusIdx, setFocusIdx] = useState(-1);
  const digits = (value || '').padEnd(length).slice(0, length).split('');
  const set = (i, ch) => {
    const arr = (value || '').split('');
    arr[i] = ch;
    onChange(arr.join('').slice(0, length));
  };
  return (
    <div style={{ display: 'flex', gap: 10, direction: 'ltr', justifyContent: 'center' }}>
      {Array.from({ length }).map((_, i) => {
        const filled = !!digits[i].trim();
        const focused = focusIdx === i;
        return (
          <input
            key={i}
            ref={el => { refs.current[i] = el; }}
            value={digits[i].trim()}
            type="number"
            className="otp-digit"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={`رقم ${i + 1}`}
            onFocus={() => setFocusIdx(i)}
            onBlur={() => setFocusIdx(-1)}
            onChange={e => {
              const ch = e.target.value.replace(/[۰-۹]/g, c => '۰۱۲۳۴۵۶۷۸۹'.indexOf(c)).replace(/[٠-٩]/g, c => '٠١٢٣٤٥٦٧٨٩'.indexOf(c)).replace(/[^0-9]/g, '').slice(-1);
              set(i, ch);
              if (ch && refs.current[i + 1]) refs.current[i + 1].focus();
            }}
            onKeyDown={e => {
              if (e.key === 'Backspace' && !digits[i].trim() && i > 0 && refs.current[i - 1]) refs.current[i - 1].focus();
            }}
            onPaste={e => {
              e.preventDefault();
              const t = e.clipboardData.getData('text').replace(/[۰-۹]/g, c => '۰۱۲۳۴۵۶۷۸۹'.indexOf(c)).replace(/[٠-٩]/g, c => '٠١٢٣٤٥٦٧٨٩'.indexOf(c)).replace(/[^0-9]/g, '').slice(0, length);
              if (!t) return;
              onChange(t);
              const nx = refs.current[Math.min(t.length, length - 1)];
              if (nx) nx.focus();
            }}
            style={{
              width: 52, height: 62, textAlign: 'center', fontSize: '1.5rem', fontWeight: 800,
              borderRadius: 16, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              border: `2px solid ${focused ? 'var(--primary)' : filled ? 'var(--accent)' : 'var(--border)'}`,
              background: filled && !focused ? 'var(--muted)' : 'var(--card)', color: 'var(--fg)',
              boxShadow: focused ? '0 0 0 4px rgba(161,98,7,0.15)' : 'none',
              transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
            }}
          />
        );
      })}
    </div>
  );
}

const STEPS = ['شماره موبایل', 'تایید', 'رمز عبور'];
const STEP_IDX = { phone: 0, password: 1, otp: 1, newpass: 2, forgot: 1, forgotcode: 2 };

export default function CustomerAccount({ onDone, initialPhone, compact }) {
  const [step, setStep] = useState('phone'); // phone|password|otp|newpass|forgot|forgotcode
  const [phone, setPhone] = useState(initialPhone || '');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [vToken, setVToken] = useState('');
  const [codeInfo, setCodeInfo] = useState('');
  const [referral, setReferral] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const boxStyle = { maxWidth: '560px', margin: compact ? '0 auto' : '40px auto', padding: compact ? 0 : '32px 28px', borderRadius: 16, boxShadow: compact ? 'none' : '0 8px 30px rgba(28,25,23,0.08)', backgroundColor: compact ? 'transparent' : 'var(--card)', direction: 'rtl', textAlign: 'right', borderTop: compact ? 'none' : '4px solid #A16207' };

  const err = e => {
    const d = e.response?.data || {};
    const firstErr = d.errors ? Object.values(d.errors).flat()[0] : null;
    toast.error(faError(d.message || firstErr || e.message), { autoClose: 5000 });
  };
  const normPhone = v => {
    let d = String(v || '').replace(/[۰-۹]/g, c => '۰۱۲۳۴۵۶۷۸۹'.indexOf(c)).replace(/[٠-٩]/g, c => '٠١٢٣٤٥٦٧٨٩'.indexOf(c)).replace(/[^0-9]/g, '');
    if (d.startsWith('0098')) d = d.slice(4);
    if (d.startsWith('98') && d.length === 12) d = d.slice(2);
    if (d.length === 10 && d.startsWith('9')) d = '0' + d;
    return d;
  };
  const okPhone = (p) => {
    const v = p !== undefined ? p : normPhone(phone);
    if (!/^09[0-9]{9}$/.test(v)) { toast.error('شماره موبایل معتبر نیست (مثل 09123456789).', { autoClose: 5000 }); return null; }
    return v;
  };

  const finish = (token, user) => {
    try {
      localStorage.setItem('customer_token', token);
      localStorage.setItem('customer_phone', user.phone);
    } catch {}
    toast.success('خوش آمدید!', { autoClose: 3000 });
    onDone({ phone: user.phone, token, user });
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      const s = await axios.post('/auth/send-otp', { phone });
      if (s.data.dev_code) setCodeInfo(`کد شما: ${s.data.dev_code}`);
      toast.success('کد جدید ارسال شد.', { autoClose: 5000 });
      setCooldown(60);
    } catch (e) { err(e); }
    setBusy(false);
  };

  const resendBtn = (
    <div style={{ marginTop: 14, textAlign: 'center' }}>
      <button onClick={resend} disabled={cooldown > 0 || busy} style={{ ...linkBtn, opacity: cooldown > 0 || busy ? 0.5 : 1, cursor: cooldown > 0 || busy ? 'default' : 'pointer' }}>
        {cooldown > 0 ? `ارسال مجدد کد تا ${cooldown} ثانیه دیگر` : 'ارسال مجدد کد'}
      </button>
    </div>
  );

  const BusyBtn = ({ onClick, children }) => (
    <button onClick={onClick} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}>
      {busy ? '⏳ لطفاً صبر کنید...' : children}
    </button>
  );

  const StepHead = ({ Icon, title, sub }) => (
    <div style={{ textAlign: 'center', marginBottom: 22 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '1.2rem', color: 'var(--card-fg, var(--fg))' }}>{title}</h2>
      <p style={{ color: 'var(--muted-fg)', margin: 0, fontSize: '0.86em' }}>{sub}</p>
    </div>
  );

  const CodeBox = codeInfo ? (
    <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 14, background: 'var(--muted)', border: '1.5px dashed var(--border)', textAlign: 'center', fontWeight: 800, fontSize: '1.05rem', color: 'var(--accent)', letterSpacing: 1 }}>{codeInfo}</div>
  ) : null;

  const submitPhone = async () => {
    const ph = okPhone();
    if (!ph) return;
    setPhone(ph);
    setBusy(true);
    try {
      const r = await axios.post('/auth/check', { phone: ph });
      if (r.data.exists) setStep('password');
      else {
        const s = await axios.post('/auth/send-otp', { phone: ph });
        if (s.data.dev_code) setCodeInfo(`کد شما: ${s.data.dev_code}`);
        else setCodeInfo('');
        toast.success('کد تایید ارسال شد.', { autoClose: 5000 });
        setStep('otp');
        setCooldown(60);
      }
    } catch (e) { err(e); }
    setBusy(false);
  };

  const submitPassword = async () => {
    setBusy(true);
    try {
      const r = await axios.post('/auth/login', { phone, password });
      finish(r.data.token, r.data.user);
    } catch (e) { err(e); }
    setBusy(false);
  };

  const submitOtp = async () => {
    if ((otp || '').length < 5) { toast.error('کد تایید ۵ رقمی را کامل وارد کنید.', { autoClose: 5000 }); return; }
    setBusy(true);
    try {
      const r = await axios.post('/auth/verify-otp', { phone, code: otp });
      setVToken(r.data.verification_token);
      setCodeInfo('');
      setStep('newpass');
    } catch (e) { err(e); }
    setBusy(false);
  };

  const submitNewPass = async () => {
    if ((password || '').length < 6) { toast.error('رمز عبور حداقل ۶ کاراکتر باشد.', { autoClose: 5000 }); return; }
    setBusy(true);
    try {
      const r = await axios.post('/auth/register', { phone, password, verification_token: vToken, referral: referral.trim() || undefined });
      setPassword('');
      toast.success('حساب شما ساخته شد!', { autoClose: 5000 });
      finish(r.data.token, r.data.user);
    } catch (e) { err(e); }
    setBusy(false);
  };

  const submitForgot = async () => {
    setBusy(true);
    try {
      const s = await axios.post('/auth/send-otp', { phone });
      if (s.data.dev_code) setCodeInfo(`کد شما: ${s.data.dev_code}`);
      else setCodeInfo('');
      toast.success('کد بازیابی ارسال شد.', { autoClose: 5000 });
      setStep('forgotcode');
      setCooldown(60);
    } catch (e) { err(e); }
    setBusy(false);
  };

  const submitForgotCode = async () => {
    if ((otp || '').length < 5) { toast.error('کد تایید ۵ رقمی را کامل وارد کنید.', { autoClose: 5000 }); return; }
    if ((password || '').length < 6) { toast.error('رمز عبور حداقل ۶ کاراکتر باشد.', { autoClose: 5000 }); return; }
    setBusy(true);
    try {
      const r = await axios.post('/auth/reset-password', { phone, code: otp, password });
      setPassword('');
      setCodeInfo('');
      toast.success('رمز جدید ثبت شد؛ خوش آمدید!', { autoClose: 5000 });
      finish(r.data.token, r.data.user);
    } catch (e) { err(e); }
    setBusy(false);
  };

  return (
    <div>
      {step === 'phone' && (
        <div style={boxStyle}>
          <StepHead Icon={Smartphone} title="ورود به حساب" sub="شماره موبایل خود را وارد کنید" />
          <div style={{ marginBottom: '15px' }}>
            <input type="text" value={phone} onChange={e => setPhone(normPhone(e.target.value).slice(0, 11))} placeholder="09123456789" style={{ ...inStyle, textAlign: 'center', fontSize: '1.05rem', letterSpacing: 1 }} dir="ltr" inputMode="tel" />
          </div>
          <button onClick={submitPhone} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}>{busy ? '⏳ در حال ارسال...' : 'ادامه ←'}</button>
        </div>
      )}

      {step === 'password' && (
        <div style={boxStyle}>
          <StepHead Icon={KeyRound} title="رمز عبور" sub={`برای شماره ${phone} رمز عبور را وارد کنید`} />
          <div style={{ marginBottom: '15px' }}>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="رمز عبور" style={inStyle} />
          </div>
          <BusyBtn onClick={submitPassword}>ورود به حساب</BusyBtn>
          <div style={{ marginTop: 14, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => setStep('forgot')} style={linkBtn}>فراموشی رمز عبور</button>
            <span style={{ color: 'var(--border)' }}>|</span>
            <button onClick={() => setStep('phone')} style={linkBtn}>تغییر شماره</button>
          </div>
        </div>
      )}

      {step === 'otp' && (
        <div style={boxStyle}>
          <StepHead Icon={ShieldCheck} title="کد تایید" sub={`کد پیامک‌شده به ${phone} را وارد کنید`} />
          <div style={{ marginBottom: '18px' }}>
            <OtpBoxes value={otp} onChange={setOtp} />
          </div>
          <BusyBtn onClick={submitOtp}>تایید و ادامه</BusyBtn>
          {resendBtn}
          {CodeBox}
        </div>
      )}

      {step === 'newpass' && (
        <div style={boxStyle}>
          <StepHead Icon={KeyRound} title="انتخاب رمز عبور" sub="برای حساب خود یک رمز عبور انتخاب کنید" />
          <div style={{ marginBottom: '15px' }}>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="حداقل ۶ کاراکتر" style={inStyle} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <input value={referral} onChange={e => setReferral(e.target.value.replace(/[^0-9]/g, ''))} placeholder="کد معرف (اختیاری)" inputMode="numeric" style={inStyle} />
          </div>
          <BusyBtn onClick={submitNewPass}>ساخت حساب کاربری</BusyBtn>
        </div>
      )}

      {step === 'forgot' && (
        <div style={boxStyle}>
          <StepHead Icon={RotateCcw} title="بازیابی رمز عبور" sub={`کد بازیابی به شماره ${phone} ارسال می‌شود`} />
          <BusyBtn onClick={submitForgot}>ارسال کد بازیابی</BusyBtn>
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button onClick={() => setStep('password')} style={linkBtn}>← بازگشت به ورود</button>
          </div>
        </div>
      )}

      {step === 'forgotcode' && (
        <div style={boxStyle}>
          <StepHead Icon={RotateCcw} title="رمز جدید" sub="کد بازیابی و رمز عبور جدید را وارد کنید" />
          <div style={{ marginBottom: '18px' }}>
            <OtpBoxes value={otp} onChange={setOtp} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="رمز جدید (حداقل ۶ کاراکتر)" style={inStyle} />
          </div>
          <BusyBtn onClick={submitForgotCode}>ثبت رمز جدید</BusyBtn>
          {resendBtn}
          {CodeBox}
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={true} rtl={true} theme="colored" />
    </div>
  );
}